import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';
import InitialsAvatar from 'design-system/src/components/InitialsAvatar/InitialsAvatar';
import Button, {
  baseButtonStyles,
  iconButtonStyles,
  smallButtonStyles,
} from 'design-system/src/components/Button/Button';
import {
  PopoverMenu,
  PopoverMenuButton,
  PopoverMenuSection,
  PopoverMenuItems,
  PopoverMenuItem,
  PopoverMenuItemButton,
} from 'design-system/src/components/PopoverMenu/PopoverMenu';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { standardTransitionStyles, focusVisibleStyles } from 'design-system/src/sharedStyles';
import HorizontalMenu from 'design-system/src/assets/icons/horizontalMenu.svg?react';
import { Dropdown, DropdownItem } from '../Dropdown.tsx';
import { SearchField } from '../Input';
import { client } from '../../api';
import type { OrgData, OrgMember } from '../../api';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { Table, TableCell, TableContainer } from '../Table';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { emptyTableCellCopy, noResultsCopy } from 'design-system/src/sharedCopy';
import AddMembersModal from './AddMembersModal/AddMembersModal';
import EditMemberModal from './EditMemberModal/EditMemberModal';
import DeleteMemberModal from './DeleteMemberModal/DeleteMemberModal';
import ManageNotificationsModal from './ManageNotificationsModal/ManageNotificationsModal';
import { formatStandardDate } from 'shared-utils/utils/formatDate';
import { roles } from '../signup/utils.tsx';

export const StyledSearchField = styled(SearchField)`
  width: 300px;
`;

const StyledDropdown = styled(Dropdown)`
  min-width: 180px;
`;

const TopButtonRowPipe = styled.div`
  width: 1px;
  height: 44px;
  margin: 0 8px;
  background-color: var(--grayscale-100);
`;

export const CustomTableContainer = styled(TableContainer)`
  margin: 1rem 0;
  height: auto;
`;

export const CustomTableRow = styled.tr`
  border-bottom: 1px solid var(--grayscale-50);
`;

export const CustomTableCell = styled(TableCell)`
  padding: 0.5rem 1.5rem 0.5rem 0.5rem;
  width: 50%;
`;

const PermissionsCell = styled(CustomTableCell)`
  min-width: 100px;
  width: auto;
`;

export const Name = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 272px;
`;

export const EmailAndName = styled.div`
  display: flex;
  flex-direction: column;
`;

export const RightmostCell = styled(CustomTableCell)`
  display: flex;
  justify-content: flex-end;
  // no right padding so the cell content is right-aligned with "Add users" button above the table
  padding-right: 0;
  gap: 8px;
  width: 182px;
  box-sizing: border-box;
`;

const LastLogin = styled(Typography)`
  border-right: 1px solid var(--grayscale-100);
  text-align: right;
  padding-right: 16px;
`;

export const IconPopoverMenuButton = styled(PopoverMenuButton)`
  ${baseButtonStyles}
  ${smallButtonStyles}
  ${iconButtonStyles}
  ${standardTransitionStyles}
  ${focusVisibleStyles}
`;

const MemberActionsPopoverMenu: React.FC<{
  isCurrentUser: boolean;
  onSelectEditMember: () => void;
  onSelectManageNotifications: () => void;
  onSelectDeleteMember: () => void;
}> = ({ isCurrentUser, onSelectEditMember, onSelectManageNotifications, onSelectDeleteMember }) => {
  return (
    <PopoverMenu>
      <IconPopoverMenuButton>
        <HorizontalMenu title="actions" />
      </IconPopoverMenuButton>

      <PopoverMenuItems width="202px">
        <PopoverMenuSection>
          <PopoverMenuItem>
            {(itemProps) => (
              <PopoverMenuItemButton
                {...itemProps}
                onClick={() => {
                  itemProps.close?.();
                  onSelectEditMember();
                }}
              >
                Edit user information
              </PopoverMenuItemButton>
            )}
          </PopoverMenuItem>

          <PopoverMenuItem>
            {(itemProps) => (
              <PopoverMenuItemButton
                {...itemProps}
                onClick={() => {
                  itemProps.close?.();
                  onSelectManageNotifications();
                }}
              >
                Manage notifications
              </PopoverMenuItemButton>
            )}
          </PopoverMenuItem>

          {!isCurrentUser && (
            <PopoverMenuItem>
              {(itemProps) => (
                <PopoverMenuItemButton
                  {...itemProps}
                  onClick={() => {
                    itemProps.close?.();
                    onSelectDeleteMember();
                  }}
                >
                  Delete
                </PopoverMenuItemButton>
              )}
            </PopoverMenuItem>
          )}
        </PopoverMenuSection>
      </PopoverMenuItems>
    </PopoverMenu>
  );
};

/**
 * Table of all users in an organization.
 */
const UsersTable: React.FC<{ isViewOnly: boolean }> = ({ isViewOnly }) => {
  const [filteredMembers, setFilteredMembers] = useState<OrgMember[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMember, setSelectedMember] = useState<OrgMember | null>(null);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState(false);
  const [showDeleteMemberModal, setShowDeleteMemberModal] = useState(false);
  const [showManageNotificationsModal, setShowManageNotificationsModal] = useState(false);
  const [activeMemberFilter, setActiveMemberFilter] = useState<string>('all');

  const orgQuery = useQuery<OrgData>({
    queryKey: ['orgMembers'],
    queryFn: () => {
      return client.get(`org-members`).then((res) => res.data);
    },
  });

  useEffect(() => {
    setFilteredMembers(
      orgQuery.data?.members.filter((member) => {
        if (activeMemberFilter === 'inactive' && !!member.last_login) {
          return false;
        }
        if (activeMemberFilter === 'active' && !member.last_login) {
          return false;
        }

        const nameInSearchQuery = member.name?.toLowerCase().includes(searchQuery.toLowerCase());
        const emailInSearchQuery = member.email_address.toLowerCase().includes(searchQuery.toLowerCase());
        return nameInSearchQuery || emailInSearchQuery;
      }) || [],
    );
  }, [orgQuery.data, searchQuery, activeMemberFilter]);

  let body;
  if (orgQuery.isLoading) {
    body = <LoadingSpinner />;
  } else if (orgQuery.error) {
    body = <PageLoadError />;
  } else if (!orgQuery.data) {
    body = null;
  } else {
    body = (
      <>
        <ButtonRow align={Alignment.SpaceBetween}>
          <StyledSearchField
            label="Search by name or email"
            placeholder="Search by name or email"
            hideLabel
            value={searchQuery}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(event.target.value)}
          />

          <ButtonRow>
            <StyledDropdown
              label="User activity status"
              onChange={(event) => {
                setActiveMemberFilter(event.target.value as string);
              }}
              value={activeMemberFilter}
            >
              <DropdownItem value="all">All users</DropdownItem>
              <DropdownItem value="active">Active users</DropdownItem>
              <DropdownItem value="inactive">Inactive users</DropdownItem>
            </StyledDropdown>
            <TopButtonRowPipe />
            <Tooltip
              content="Only an admin can add other users"
              childNotInteractive={isViewOnly}
              disabled={!isViewOnly}
            >
              <Button size="lg" onClick={() => setShowAddMembersModal(true)} disabled={isViewOnly}>
                Add users
              </Button>
            </Tooltip>
          </ButtonRow>
        </ButtonRow>

        <CustomTableContainer>
          <Table>
            <thead className="sr-only">
              <tr>
                <td>Name and email</td>
                <td>Role</td>
                <td>Permissions</td>
                <td>Last login and actions</td>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.length === 0 ? (
                <tr>
                  <CustomTableCell colSpan={3}>{noResultsCopy}</CustomTableCell>
                </tr>
              ) : (
                filteredMembers.map((member) => {
                  const { name, email_address, is_admin, id, is_current_user, last_login, job_title } = member;
                  const nameString = is_current_user ? `${name} (You)` : name;

                  return (
                    <CustomTableRow key={id}>
                      <CustomTableCell>
                        <Name>
                          <InitialsAvatar name={name || email_address} />
                          <EmailAndName>
                            <Typography renderedAs="span" styledAs="bodySmallDMSans" weight={600}>
                              {nameString}
                            </Typography>
                            <Typography
                              renderedAs="span"
                              styledAs="bodyExtraSmallDMSans"
                              weight={nameString ? 400 : 600}
                            >
                              {email_address}
                            </Typography>
                          </EmailAndName>
                        </Name>
                      </CustomTableCell>

                      <CustomTableCell>
                        <Typography styledAs="bodySmallDMSans">
                          {job_title ? roles[job_title] : emptyTableCellCopy}
                        </Typography>
                      </CustomTableCell>

                      <PermissionsCell>
                        <Typography styledAs="bodySmallDMSans">{is_admin ? 'Admin' : 'Non-admin'}</Typography>
                      </PermissionsCell>

                      <RightmostCell>
                        <LastLogin styledAs="bodyExtraSmallDMSans" color="var(--grayscale-600)">
                          Last seen: {last_login ? formatStandardDate(last_login) : 'Never'}
                        </LastLogin>
                        {!isViewOnly && (
                          <MemberActionsPopoverMenu
                            isCurrentUser={is_current_user}
                            onSelectEditMember={() => {
                              setSelectedMember(member);
                              setShowEditMemberModal(true);
                            }}
                            onSelectManageNotifications={() => {
                              setSelectedMember(member);
                              setShowManageNotificationsModal(true);
                            }}
                            onSelectDeleteMember={() => {
                              setSelectedMember(member);
                              setShowDeleteMemberModal(true);
                            }}
                          />
                        )}
                      </RightmostCell>
                    </CustomTableRow>
                  );
                })
              )}
            </tbody>
          </Table>
        </CustomTableContainer>

        {showAddMembersModal && (
          <AddMembersModal
            onClose={() => {
              setShowAddMembersModal(false);
            }}
            members={orgQuery.data.members}
          />
        )}

        {showEditMemberModal && selectedMember && (
          <EditMemberModal
            onClose={() => {
              setSelectedMember(null);
              setShowEditMemberModal(false);
            }}
            members={orgQuery.data.members}
            selectedMember={selectedMember}
          />
        )}

        {showDeleteMemberModal && selectedMember && (
          <DeleteMemberModal
            onClose={() => {
              setSelectedMember(null);
              setShowDeleteMemberModal(false);
            }}
            selectedMember={selectedMember}
          />
        )}

        {showManageNotificationsModal && selectedMember && (
          <ManageNotificationsModal
            onClose={() => {
              setSelectedMember(null);
              setShowManageNotificationsModal(false);
            }}
            memberId={selectedMember.id}
            memberName={selectedMember.name}
          />
        )}
      </>
    );
  }

  return body;
};

export default UsersTable;
